(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const reveal = (scope = document) => {
    const elements = [...scope.querySelectorAll('[data-eb-reveal]:not(.is-visible)')];
    if (!elements.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -7% 0px', threshold: 0.08 }
    );

    elements.forEach((element) => observer.observe(element));
  };

  const initializeIntro = () => {
    const intro = document.querySelector('[data-eb-intro]');
    if (!intro) {
      root.classList.remove('eb-intro-pending');
      return;
    }

    const video = intro.querySelector('[data-eb-intro-video]');
    const skip = intro.querySelector('[data-eb-intro-skip]');
    let finished = false;

    let hasSeenIntro = false;
    try {
      hasSeenIntro = window.sessionStorage.getItem('ethan-bing-film-v3') === 'seen';
    } catch (error) {
      hasSeenIntro = false;
    }

    const finish = () => {
      if (finished) return;
      finished = true;
      intro.classList.add('is-finished');
      root.classList.remove('eb-intro-pending');
      try {
        window.sessionStorage.setItem('ethan-bing-film-v3', 'seen');
      } catch (error) {
        // The reveal remains functional when storage is unavailable.
      }
      window.setTimeout(() => intro.remove(), 900);
    };

    if (hasSeenIntro || reducedMotion || root.classList.contains('shopify-design-mode')) {
      intro.classList.add('is-skipped');
      root.classList.remove('eb-intro-pending');
      return;
    }

    window.requestAnimationFrame(() => intro.classList.add('is-active'));
    skip?.addEventListener('click', finish, { once: true });

    if (video) {
      video.addEventListener('ended', finish, { once: true });
      video.addEventListener('error', finish, { once: true });
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          intro.classList.add('has-fallback');
          window.setTimeout(finish, 2200);
        });
      }
    } else {
      intro.classList.add('has-fallback');
      window.setTimeout(finish, 2200);
    }

    // Never leave a visitor behind the film if a browser fails to emit `ended`.
    window.setTimeout(finish, 7500);
  };

  const initializeHeroMotion = (scope = document) => {
    if (reducedMotion || !finePointer) return;

    scope.querySelectorAll('[data-eb-hero]:not([data-eb-motion-ready])').forEach((hero) => {
      hero.setAttribute('data-eb-motion-ready', 'true');

      const update = (event) => {
        const bounds = hero.getBoundingClientRect();
        const localX = (event.clientX - bounds.left) / bounds.width;
        const localY = (event.clientY - bounds.top) / bounds.height;
        const x = Math.max(0, Math.min(1, localX));
        const y = Math.max(0, Math.min(1, localY));

        hero.style.setProperty('--eb-pointer-x', `${(x * 100).toFixed(2)}%`);
        hero.style.setProperty('--eb-pointer-y', `${(y * 100).toFixed(2)}%`);
        hero.style.setProperty('--eb-tilt-x', `${((0.5 - y) * 3.2).toFixed(2)}deg`);
        hero.style.setProperty('--eb-tilt-y', `${((x - 0.5) * 4.5).toFixed(2)}deg`);
        hero.style.setProperty('--eb-shift-x', `${((x - 0.5) * 16).toFixed(2)}px`);
        hero.style.setProperty('--eb-shift-y', `${((y - 0.5) * 10).toFixed(2)}px`);
      };

      const reset = () => {
        hero.style.setProperty('--eb-pointer-x', '58%');
        hero.style.setProperty('--eb-pointer-y', '42%');
        hero.style.setProperty('--eb-tilt-x', '0deg');
        hero.style.setProperty('--eb-tilt-y', '0deg');
        hero.style.setProperty('--eb-shift-x', '0px');
        hero.style.setProperty('--eb-shift-y', '0px');
      };

      hero.addEventListener('pointermove', update, { passive: true });
      hero.addEventListener('pointerleave', reset, { passive: true });
    });
  };

  const initializeAtmosphere = (scope = document) => {
    if (reducedMotion) return;

    scope.querySelectorAll('[data-eb-atmosphere]:not([data-eb-canvas-ready])').forEach((canvas) => {
      const context = canvas.getContext('2d');
      if (!context) return;
      canvas.setAttribute('data-eb-canvas-ready', 'true');

      let width = 0;
      let height = 0;
      let ratio = 1;
      let particles = [];
      let frame = 0;

      const createParticle = (initial = false) => ({
        x: Math.random() * width,
        y: initial ? Math.random() * height : height + Math.random() * 60,
        radius: Math.random() * 1.15 + 0.2,
        speed: Math.random() * 0.16 + 0.045,
        drift: (Math.random() - 0.5) * 0.06,
        alpha: Math.random() * 0.32 + 0.06,
        amber: Math.random() > 0.72,
      });

      const resize = () => {
        const bounds = canvas.getBoundingClientRect();
        width = Math.max(1, bounds.width);
        height = Math.max(1, bounds.height);
        ratio = Math.min(window.devicePixelRatio || 1, 1.5);
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        const count = Math.min(56, Math.max(24, Math.round(width / 28)));
        particles = Array.from({ length: count }, () => createParticle(true));
      };

      const draw = () => {
        if (!canvas.isConnected) {
          window.cancelAnimationFrame(frame);
          return;
        }

        context.clearRect(0, 0, width, height);
        particles.forEach((particle, index) => {
          particle.y -= particle.speed;
          particle.x += particle.drift;
          if (particle.y < -12 || particle.x < -12 || particle.x > width + 12) {
            particles[index] = createParticle();
            return;
          }

          context.beginPath();
          context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          context.fillStyle = particle.amber
            ? `rgba(176, 116, 57, ${particle.alpha})`
            : `rgba(224, 218, 207, ${particle.alpha})`;
          context.fill();
        });
        frame = window.requestAnimationFrame(draw);
      };

      resize();
      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(canvas);
      } else {
        window.addEventListener('resize', resize, { passive: true });
      }
      draw();
    });
  };

  const initializeMagneticLinks = (scope = document) => {
    if (reducedMotion || !finePointer) return;

    scope.querySelectorAll('[data-eb-magnetic]:not([data-eb-magnetic-ready])').forEach((link) => {
      link.setAttribute('data-eb-magnetic-ready', 'true');
      const label = link.querySelector('span') || link;

      link.addEventListener('pointermove', (event) => {
        const bounds = link.getBoundingClientRect();
        const x = (event.clientX - bounds.left - bounds.width / 2) * 0.12;
        const y = (event.clientY - bounds.top - bounds.height / 2) * 0.16;
        label.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
      link.addEventListener('pointerleave', () => {
        label.style.transform = 'translate3d(0, 0, 0)';
      });
    });
  };

  const initializeCursor = () => {
    const cursor = document.querySelector('[data-eb-cursor]');
    const hoverPointer = window.matchMedia('(hover: hover)').matches;
    if (!cursor || reducedMotion || !finePointer || !hoverPointer || cursor.dataset.ebMercuryReady) return;

    cursor.dataset.ebMercuryReady = 'true';

    const dot = document.createElement('span');
    dot.className = 'eb-cursor-dot';
    dot.setAttribute('aria-hidden', 'true');
    document.body.append(dot);

    const canvas = document.createElement('canvas');
    canvas.className = 'eb-cursor-trail';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.append(canvas);

    const context = canvas.getContext('2d');
    if (!context) {
      dot.remove();
      canvas.remove();
      return;
    }

    root.classList.add('eb-has-cursor');

    const state = {
      x: -100,
      y: -100,
      ringX: -100,
      ringY: -100,
      visible: false,
      hasPointer: false,
      interactive: false,
    };

    const trail = [];
    const wakes = [];
    const ripples = [];
    const trailLifetime = 390;
    const wakeLifetime = 360;
    const rippleLifetime = 690;
    const interactiveSelector = [
      'a',
      'button',
      'input',
      'select',
      'textarea',
      'summary',
      '[role="button"]',
      '[data-eb-magnetic]',
    ].join(',');

    let width = window.innerWidth;
    let height = window.innerHeight;
    let pixelRatio = 1;
    let frameId = 0;
    let previousTime = performance.now();
    let lastTrailX = -100;
    let lastTrailY = -100;
    let lastWakeTime = 0;

    const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
    const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

    const resizeCursorCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const setCursorVisible = (visible) => {
      state.visible = visible;
      cursor.classList.toggle('is-visible', visible);
      dot.classList.toggle('is-visible', visible);
    };

    const setCursorInteractive = (target) => {
      const interactive = target instanceof Element && Boolean(target.closest(interactiveSelector));
      if (interactive === state.interactive) return;

      state.interactive = interactive;
      cursor.classList.toggle('is-interactive', interactive);
      dot.classList.toggle('is-interactive', interactive);
    };

    const drawTrail = (now) => {
      while (trail.length && now - trail[0].born > trailLifetime) trail.shift();
      if (trail.length < 2) return;

      const newest = trail[trail.length - 1];
      const fade = clamp(1 - (now - newest.born) / trailLifetime, 0, 1);
      if (fade <= 0) return;

      const first = trail[0];
      const gradient = context.createLinearGradient(first.x, first.y, newest.x, newest.y);
      gradient.addColorStop(0, 'rgba(216, 208, 196, 0)');
      gradient.addColorStop(0.58, `rgba(216, 208, 196, ${0.08 * fade})`);
      gradient.addColorStop(1, `rgba(242, 238, 232, ${0.22 * fade})`);

      context.save();
      context.beginPath();
      context.moveTo(first.x, first.y);

      for (let index = 1; index < trail.length; index += 1) {
        const point = trail[index];
        const next = trail[index + 1] || point;
        const midpointX = (point.x + next.x) * 0.5;
        const midpointY = (point.y + next.y) * 0.5;
        context.quadraticCurveTo(point.x, point.y, midpointX, midpointY);
      }

      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = 1;
      context.strokeStyle = gradient;
      context.stroke();
      context.restore();
    };

    const drawWakes = (now) => {
      for (let index = wakes.length - 1; index >= 0; index -= 1) {
        const wake = wakes[index];
        const progress = (now - wake.born) / wakeLifetime;

        if (progress >= 1) {
          wakes.splice(index, 1);
          continue;
        }

        const eased = easeOutCubic(clamp(progress, 0, 1));
        const radius = 2 + eased * 10;
        const alpha = Math.pow(1 - progress, 1.8) * 0.105;

        context.save();
        context.beginPath();
        context.arc(wake.x, wake.y, radius, 0, Math.PI * 2);
        context.lineWidth = 0.7;
        context.strokeStyle = `rgba(216, 208, 196, ${alpha})`;
        context.stroke();
        context.restore();
      }
    };

    const drawRipples = (now) => {
      for (let index = ripples.length - 1; index >= 0; index -= 1) {
        const ripple = ripples[index];
        const elapsed = now - ripple.born - ripple.delay;
        if (elapsed < 0) continue;

        const progress = elapsed / rippleLifetime;
        if (progress >= 1) {
          ripples.splice(index, 1);
          continue;
        }

        const eased = easeOutCubic(clamp(progress, 0, 1));
        const radius = 7 + eased * ripple.distance;
        const alpha = Math.pow(1 - progress, 1.65) * ripple.alpha;

        context.save();

        if (ripple.delay === 0 && progress < 0.55) {
          const glow = context.createRadialGradient(ripple.x, ripple.y, 0, ripple.x, ripple.y, radius);
          glow.addColorStop(0, `rgba(168, 122, 67, ${alpha * 0.18})`);
          glow.addColorStop(1, 'rgba(168, 122, 67, 0)');
          context.fillStyle = glow;
          context.beginPath();
          context.arc(ripple.x, ripple.y, radius, 0, Math.PI * 2);
          context.fill();
        }

        context.beginPath();
        context.arc(ripple.x, ripple.y, radius, 0, Math.PI * 2);
        context.lineWidth = ripple.delay === 0 ? 0.9 : 0.65;
        context.strokeStyle = ripple.warm
          ? `rgba(168, 122, 67, ${alpha})`
          : `rgba(242, 238, 232, ${alpha})`;
        context.stroke();
        context.restore();
      }
    };

    const ensureCursorFrame = () => {
      if (frameId) return;
      previousTime = performance.now();
      frameId = window.requestAnimationFrame(renderCursorFrame);
    };

    function renderCursorFrame(now) {
      frameId = 0;
      const elapsed = Math.min(40, now - previousTime);
      previousTime = now;

      const follow = 1 - Math.pow(0.82, elapsed / 16.667);
      state.ringX += (state.x - state.ringX) * follow;
      state.ringY += (state.y - state.ringY) * follow;

      cursor.style.transform = `translate3d(${state.ringX}px, ${state.ringY}px, 0) translate(-50%, -50%)`;
      dot.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) translate(-50%, -50%)`;

      context.clearRect(0, 0, width, height);
      drawTrail(now);
      drawWakes(now);
      drawRipples(now);

      const ringIsMoving = Math.abs(state.x - state.ringX) + Math.abs(state.y - state.ringY) > 0.08;
      if (ringIsMoving || trail.length || wakes.length || ripples.length) ensureCursorFrame();
    }

    const handlePointerMove = (event) => {
      const now = performance.now();
      state.x = event.clientX;
      state.y = event.clientY;

      if (!state.hasPointer) {
        state.hasPointer = true;
        state.ringX = state.x;
        state.ringY = state.y;
        lastTrailX = state.x;
        lastTrailY = state.y;
      }

      const distance = Math.hypot(state.x - lastTrailX, state.y - lastTrailY);
      if (distance >= 5) {
        trail.push({ x: state.x, y: state.y, born: now });
        if (trail.length > 22) trail.shift();

        if (distance >= 8 && now - lastWakeTime > 72) {
          wakes.push({ x: state.x, y: state.y, born: now });
          if (wakes.length > 7) wakes.shift();
          lastWakeTime = now;
        }

        lastTrailX = state.x;
        lastTrailY = state.y;
      }

      setCursorVisible(true);
      setCursorInteractive(event.target);
      ensureCursorFrame();
    };

    const handlePointerDown = (event) => {
      if (event.button !== 0 || !state.hasPointer) return;

      cursor.classList.add('is-pressed');
      const born = performance.now();
      ripples.push(
        { x: event.clientX, y: event.clientY, born, delay: 0, distance: 48, alpha: 0.34, warm: false },
        { x: event.clientX, y: event.clientY, born, delay: 86, distance: 61, alpha: 0.22, warm: true }
      );
      ensureCursorFrame();
    };

    const hideCursor = () => {
      setCursorVisible(false);
      cursor.classList.remove('is-pressed', 'is-interactive');
      dot.classList.remove('is-interactive');
      state.interactive = false;
      ensureCursorFrame();
    };

    resizeCursorCanvas();
    window.addEventListener('resize', resizeCursorCanvas, { passive: true });
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.addEventListener('pointerover', (event) => setCursorInteractive(event.target), { passive: true });
    document.addEventListener('pointerdown', handlePointerDown, { passive: true });
    document.addEventListener('pointerup', () => cursor.classList.remove('is-pressed'), { passive: true });
    document.addEventListener('mouseout', (event) => {
      if (!event.relatedTarget) hideCursor();
    });
    window.addEventListener('blur', hideCursor);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) hideCursor();
    });
  };

  const initializeScrollProgress = () => {
    const progress = document.querySelector('[data-eb-scroll-progress]');
    if (!progress) return;
    let scheduled = false;

    const update = () => {
      const maximum = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = maximum > 0 ? window.scrollY / maximum : 0;
      progress.style.transform = `scaleX(${Math.max(0, Math.min(1, ratio))})`;
      scheduled = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  const initialize = (scope = document) => {
    reveal(scope);
    initializeHeroMotion(scope);
    initializeAtmosphere(scope);
    initializeMagneticLinks(scope);
  };

  root.classList.add('eb-motion-ready');
  onReady(() => {
    initializeIntro();
    initialize();
    initializeCursor();
    initializeScrollProgress();
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
